import React, { useState, useEffect } from 'react';
import { Box, Typography, Alert, Snackbar } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import RulesList from '../components/rules/RulesList';
import RuleBuilder from '../components/rules/RuleBuilder';
import RuleTestDialog from '../components/rules/RuleTestDialog';
import RuleHistoryDialog from '../components/rules/RuleHistoryDialog';
import rulesService from '../services/rulesService';
import { 
  Rule, 
  CreateRuleRequest, 
  UpdateRuleRequest, 
  RuleFilters,
  RuleTestResult 
} from '../types/rules';

type View = 'list' | 'create' | 'edit';

const RuleBuilderPage: React.FC = () => {
  const [view, setView] = useState<View>('list');
  const [rules, setRules] = useState<Rule[]>([]);
  const [selectedRule, setSelectedRule] = useState<Rule | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState<RuleFilters>({});
  
  // Dialog states
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [ruleToTest, setRuleToTest] = useState<Rule | null>(null);
  const [ruleForHistory, setRuleForHistory] = useState<Rule | null>(null);
  
  // Notification states
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    loadRules();
  }, [filters]);

  useEffect(() => {
    // Handle URL parameters for deep linking
    const ruleId = searchParams.get('ruleId');
    const action = searchParams.get('action');
    
    if (ruleId && action === 'edit') {
      const rule = rules.find(r => r.id === ruleId);
      if (rule) {
        setSelectedRule(rule);
        setView('edit');
      }
    }
  }, [searchParams, rules]);

  const loadRules = async () => {
    setLoading(true);
    try {
      const response = await rulesService.getRules(filters);
      setRules(response.rules);
    } catch (error) {
      showNotification('Failed to load rules', 'error');
      console.error('Error loading rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setNotification({ open: true, message, severity });
  };

  const handleCreateRule = () => {
    setSelectedRule(undefined);
    setView('create');
    setSearchParams({});
  };

  const handleEditRule = (rule: Rule) => {
    setSelectedRule(rule);
    setView('edit');
    setSearchParams({ ruleId: rule.id, action: 'edit' });
  };

  const handleDeleteRule = async (ruleId: string) => {
    try {
      await rulesService.deleteRule(ruleId);
      await loadRules();
      showNotification('Rule deleted successfully', 'success');
    } catch (error) {
      showNotification('Failed to delete rule', 'error');
      console.error('Error deleting rule:', error);
    }
  };

  const handleTestRule = (rule: Rule) => {
    setRuleToTest(rule);
    setTestDialogOpen(true);
  };

  const handleViewHistory = (rule: Rule) => {
    setRuleForHistory(rule);
    setHistoryDialogOpen(true);
  };

  const handleSaveRule = async (ruleData: CreateRuleRequest | UpdateRuleRequest) => {
    setSaving(true);
    try {
      if (view === 'create') {
        await rulesService.createRule(ruleData as CreateRuleRequest);
        showNotification('Rule created successfully', 'success');
      } else if (view === 'edit' && selectedRule) {
        await rulesService.updateRule(selectedRule.id, ruleData as UpdateRuleRequest);
        showNotification('Rule updated successfully', 'success');
      }
      
      await loadRules();
      setView('list');
      setSelectedRule(undefined);
      setSearchParams({});
    } catch (error) {
      showNotification(
        view === 'create' ? 'Failed to create rule' : 'Failed to update rule', 
        'error'
      );
      console.error('Error saving rule:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleTestRuleExecution = async (input: Record<string, unknown>): Promise<RuleTestResult> => {
    if (!ruleToTest) {
      throw new Error('No rule selected for testing');
    }

    try {
      return await rulesService.testRule(ruleToTest.id, input);
    } catch (error) {
      console.error('Error testing rule:', error);
      throw error;
    }
  };

  const handleGetRuleHistory = async (ruleId: string) => {
    try {
      return await rulesService.getRuleHistory(ruleId);
    } catch (error) {
      console.error('Error getting rule history:', error);
      throw error;
    }
  };

  const handleCancel = () => {
    setView('list');
    setSelectedRule(undefined);
    setSearchParams({});
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ p: 2.5 }}>
      {view === 'list' && (
        <>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
            Business Rules Engine
          </Typography>
          
          <RulesList
            rules={rules}
            loading={loading}
            onEdit={handleEditRule}
            onDelete={handleDeleteRule}
            onTest={handleTestRule}
            onViewHistory={handleViewHistory}
            onCreate={handleCreateRule}
            onRefresh={loadRules}
            filters={filters}
            onFiltersChange={setFilters}
          />
        </>
      )}

      {(view === 'create' || view === 'edit') && (
        <RuleBuilder
          rule={selectedRule}
          onSave={handleSaveRule}
          onTest={view === 'edit' ? (input) => handleTestRuleExecution(input) : undefined}
          onCancel={handleCancel}
          isLoading={saving}
        />
      )}

      {/* Test Dialog */}
      <RuleTestDialog
        open={testDialogOpen}
        rule={ruleToTest}
        onClose={() => {
          setTestDialogOpen(false);
          setRuleToTest(null);
        }}
        onTest={handleTestRuleExecution}
      />

      {/* History Dialog */}
      <RuleHistoryDialog
        open={historyDialogOpen}
        rule={ruleForHistory}
        onClose={() => {
          setHistoryDialogOpen(false);
          setRuleForHistory(null);
        }}
        onGetHistory={handleGetRuleHistory}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RuleBuilderPage;